import { withoutBody } from "@/lib/blog";
import type { GetStaticPaths, GetStaticProps } from "next";
import Link from "next/link";
import Layout from "@/components/Layout";
import TreatmentSections from "@/components/TreatmentSections";
import TreatmentSidebar from "@/components/TreatmentSidebar";
import { getTreatment, getTreatments, getTreatmentNav, getPosts } from "@/lib/cms";
import { imageUrl, type NavNode, type Treatment } from "@/lib/treatments";
import type { BlogPost } from "@/lib/blog";
import { SITE_URL } from "@/lib/seo";

// Replaces the fourteen hand-converted treatment pages with one template driven
// by the `treatments` collection. Static routes (pages/about.tsx, /contact …)
// take precedence over this dynamic one, and getStaticPaths only emits the
// treatment slugs, so nothing else is captured.
//
// The counter strip, sidebar, appointment CTA and video pair were identical on
// every page, so they are template furniture rather than CMS fields — see
// "What is not in this collection" in docs/cms-collections.md.

type Props = {
  treatment: Treatment;
  nav: NavNode[];
  recentPosts: BlogPost[];
};

const COUNTERS = [
  { icon: "icofont-patient-bed", count: 85000, label: "OPD Patients", suffix: "" },
  { icon: "icofont-people", count: 9300, label: "Happy Patients", suffix: "+" },
  { icon: "icofont-presentation", count: 200, label: "Seminar", suffix: "" },
  { icon: "icofont-doctor", count: 18, label: "Year Experience", suffix: "" },
];

// The same two videos appeared on every treatment page.
const VIDEO_IDS = ["PTwWMOweu98", "e7YXXCmJsnM"];

export default function TreatmentPage({ treatment, nav, recentPosts }: Props) {
  const hero = imageUrl(treatment.hero_image);
  // The lung-cancer page uses the bare full-width layout it has on the live
  // site: no sidebar, counter strip, appointment CTA or shared video pair.
  const bare = treatment.layout === "full";

  const faqSchema =
    treatment.faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: treatment.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null;

  return (
    <Layout
      route={`/${treatment.slug}`}
      seo={{
        title: treatment.seoTitle || treatment.title,
        description: treatment.metaDescription,
        canonical: `/${treatment.slug}`,
      }}
      schemas={faqSchema ? [faqSchema] : []}
    >
      <div className="service-details-area pb-100" style={{ background: "#fdefef" }}>
        <div
          style={{
            backgroundImage: hero ? `url(${hero})` : undefined,
            height: 500,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="container" style={{ height: "100%" }}>
            <div className="row" style={{ height: "100%" }}>
              <div className="col-md-6">
                <div
                  className="d-flex flex-column justify-content-center"
                  style={{ height: "100%", padding: 15 }}
                >
                  <h1 style={{ color: "white", fontWeight: "bold" }}>{treatment.title}</h1>
                  {treatment.subtitle && (
                    <p style={{ color: "white", fontSize: 18 }}>{treatment.subtitle}</p>
                  )}
                  <div className="d-flex flex-column flex-md-row">
                    <Link
                      href="/contact"
                      className="btn btn-primary"
                      style={{
                        margin: 5,
                        color: "#104bb3",
                        backgroundColor: "white",
                        fontWeight: "bold",
                      }}
                    >
                      Get Appointment
                    </Link>
                    <Link
                      href="/blog"
                      className="btn btn-primary"
                      style={{
                        margin: 5,
                        backgroundColor: "#104bb3",
                        border: "1px solid white",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      Learn More
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container pt-5">
          <div className="row">
            <div
              className={bare ? "col-lg-12" : "col-lg-8"}
              style={{ backgroundColor: "white", borderRadius: 10 }}
            >
              <div className="services-details-img">
                <div className="card-body p-4">
                  <h5 className="text-primary fw-bold mb-3">{treatment.title}</h5>
                  <hr />
                  {treatment.intro.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
                <TreatmentSections sections={treatment.sections} />
              </div>
            </div>

            {!bare && (
              <div className="col-lg-4">
                <TreatmentSidebar treatments={nav} recentPosts={recentPosts} />
              </div>
            )}
          </div>
        </div>

        {!bare && (
        <div className="counter-area">
          <div className="container">
            <div className="row counter-bg">
              {COUNTERS.map((c) => (
                <div className="col-sm-6 col-md-3 col-lg-3" key={c.label}>
                  <div className="counter-item">
                    <i className={c.icon} />
                    <h3>
                      <span className="odometer" data-count={c.count}>
                        00
                      </span>
                      {c.suffix}
                    </h3>
                    <p>{c.label}</p>
                  </div>
                </div>
                ))}
            </div>
          </div>
        </div>
        )}

        {!bare && (
        <div className="appointment-strip text-center py-5">
          <Link href="/contact">
            <h2 className="text-white fw-bold text-decoration-underline">
              Experiencing These Symptoms? Book Diagnostic Appointment
            </h2>
          </Link>
        </div>
        )}

        <div className="info-section py-5">
          <div className="container">
            {!bare && <h5 className="text-center text-primary fw-bold mb-4">Videos</h5>}
            <div className="row justify-content-center mb-5">
              {!bare &&
                VIDEO_IDS.map((id) => (
                <div className="col-md-6 mb-3" key={id}>
                  <div className="video-box">
                    <iframe
                      width="100%"
                      height={250}
                      src={`https://www.youtube.com/embed/${id}`}
                      title="Chest Surgery India video"
                      frameBorder="0"
                      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
                ))}
            </div>

            {treatment.faqs.length > 0 && (
              <div className="row">
                <div className="col-lg-12">
                  <h2 style={{ textAlign: "center", paddingBottom: 30 }}>FAQs</h2>
                  <div className="accordion" id="accordionExample">
                    {treatment.faqs.map((faq, i) => {
                      const id = `treatmentFaq${i}`;
                      const open = i === 0;
                      return (
                        <div className="accordion-item" key={id}>
                          <h2 className="accordion-header" id={`${id}Heading`}>
                            <button
                              className={`accordion-button${open ? "" : " collapsed"}`}
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#${id}`}
                              aria-expanded={open}
                              aria-controls={id}
                            >
                              {`${i + 1}. ${faq.q}`}
                            </button>
                          </h2>
                          <div
                            id={id}
                            className={`accordion-collapse collapse${open ? " show" : ""}`}
                            aria-labelledby={`${id}Heading`}
                            data-bs-parent="#accordionExample"
                          >
                            <div className="accordion-body">{faq.a}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const treatments = await getTreatments();
  return {
    paths: treatments.map((t) => ({ params: { treatment: t.slug } })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<Props, { treatment: string }> = async ({ params }) => {
  const treatment = await getTreatment(params!.treatment);
  if (!treatment) return { notFound: true };

  const [nav, posts] = await Promise.all([getTreatmentNav(), getPosts()]);

  return {
    props: {
      treatment,
      // The sidebar lists the top level only, as the original markup did.
      nav: nav.map(({ label, href }) => ({ label, href })),
      recentPosts: posts.slice(0, 3).map(withoutBody),
    },
  };
};
